import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, HardDrive, Play, FolderOpen } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useHardware } from "../context/HardwareContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Profiles() {
  const { user, ready } = useAuth();
  const { applyHardware } = useHardware();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    api.get("/profiles").then((r) => setProfiles(r.data.profiles)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) load();
    else setLoading(false);
  }, [user]);

  const del = async (id) => {
    await api.delete(`/profiles/${id}`);
    setProfiles((p) => p.filter((x) => x.id !== id));
    toast.success("Profil supprimé");
  };

  const use = (hw) => {
    applyHardware(hw);
    toast.success("Profil chargé dans l'analyseur");
  };

  if (ready && !user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <FolderOpen className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <h1 className="font-head text-2xl font-bold">Profils matériel</h1>
        <p className="mt-2 text-muted-foreground">Connectez-vous pour sauvegarder et retrouver vos configurations.</p>
        <Link to="/login"><Button className="mt-4" data-testid="profiles-login-cta">Se connecter</Button></Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 md:px-8">
      <h1 className="mb-6 font-head text-3xl font-extrabold tracking-tight">Mes profils matériel</h1>
      {loading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : profiles.length === 0 ? (
        <div className="rounded-xl border border-[#1E2638] bg-[#0F141C] p-10 text-center text-muted-foreground">
          Aucun profil enregistré. Depuis le Diagnostic, cliquez sur « Sauver ».
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {profiles.map((p) => (
            <div key={p.id} data-testid="profile-card" className="rounded-xl border border-[#1E2638] bg-[#0F141C] p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <HardDrive className="h-5 w-5 text-primary" />
                  <h3 className="font-head text-base font-bold">{p.name}</h3>
                </div>
                <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-red-400" data-testid="profile-delete-button">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 font-mono2 text-xs text-muted-foreground">
                <span>CPU {p.hardware.cpuCores} cœurs</span>
                <span>RAM {p.hardware.ram} Go</span>
                <span>VRAM {p.hardware.vram} Go</span>
                <span>{p.hardware.accelerator}</span>
                <span className="col-span-2 truncate text-slate-400">{p.hardware.gpu}</span>
              </div>
              <Button variant="secondary" size="sm" className="mt-4 w-full gap-1.5" onClick={() => use(p.hardware)} data-testid="profile-use-button">
                <Play className="h-3.5 w-3.5" /> Charger ce profil
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
